import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { usePageTracking } from "@/hooks/usePageTracking";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load all non-homepage routes
const ShopPage = lazy(() => import("./pages/ShopPage"));
const OfficePage = lazy(() => import("./pages/OfficePage"));
const AcademyPage = lazy(() => import("./pages/AcademyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const RoasteryPage = lazy(() => import("./pages/RoasteryPage"));
const CoffeeShopStarterPage = lazy(() => import("./pages/CoffeeShopStarterPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CoffeeDetailPage = lazy(() => import("./pages/CoffeeDetailPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

const queryClient = new QueryClient();

const PageFallback = () => <div className="min-h-screen" />;

const AppRoutes = () => {
  usePageTracking();
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/toko-kopi" element={<ShopPage />} />
        <Route path="/kopi/:slug" element={<CoffeeDetailPage />} />
        <Route path="/solusi-kantor" element={<OfficePage />} />
        <Route path="/mesin-kopi-sewa" element={<OfficePage />} />
        <Route path="/akademi" element={<AcademyPage />} />
        <Route path="/roastery" element={<RoasteryPage />} />
        <Route path="/coffee-shop-starter" element={<CoffeeShopStarterPage />} />
        <Route path="/cerita-kami" element={<AboutPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/hubungi-kami" element={<ContactPage />} />
        <Route path="/kebijakan-privasi" element={<PrivacyPolicyPage />} />
        <Route path="/syarat-ketentuan" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/berita" element={<BlogPage />} />
        <Route path="/berita/:slug" element={<ArticlePage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
